/**
 * Intake admission result panel (WP6e / #46629).
 *
 * Extracted from workflows.tsx for the ~1,000-line cap (#47040 ratchet).
 */

import type { WorkflowIntakeView } from "@excellent/core/verification-workflow-intake";
import { href } from "../nav";
import { ToneBadge } from "../ui";
import { ACTION, BIND_PREREQ, Btn } from "./workflows-contracts";

export function IntakeResult({
  view,
  bindError,
  persistError,
  onBind,
  onPersist,
  onNavigate,
  canMutate,
}: {
  view: WorkflowIntakeView;
  bindError: string | null;
  persistError: string | null;
  onBind: (key: string) => void;
  onPersist: () => void;
  onNavigate: (to: string) => void;
  canMutate: boolean;
}) {
  return (
    <>
      <div className="vp-card">
        <div className="vp-card-head">
          <div className="vp-card-title">Admission verdict</div>
          <ToneBadge
            tone={view.admission.status === "admit" ? "pass" : view.admission.status === "reject" ? "fail" : "unknown"}
          >
            {view.admission.status}
          </ToneBadge>
        </div>
        <div className="vp-card-body">
          <ul>
            {view.admission.reasons.map((reason) => (
              <li key={reason} className="vp-mono">
                {reason}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="vp-callout" data-tone="unknown">
        <div className="vp-callout-title">Intent grounding shown before admission</div>
        <div className="vp-callout-body">
          These predicates are eligible only when their source-backed intent can cite retained spans and digests from
          repository policy, requester issue text, or an observed occurrence with content permission. Presence-only,
          permission-limited, and revoked observations stay auditable but cannot supply hidden outcome text. If sources
          conflict or the request is vague, this panel must show confirmation work instead of silently accepting an
          invented contract.
        </div>
      </div>
      {view.blocked ? (
        <div className="vp-callout" data-tone="fail">
          <div className="vp-callout-title">Fail-closed: {view.blocked.reason}</div>
          <div className="vp-callout-body">{view.blocked.message}</div>
        </div>
      ) : null}
      {view.predicates.length > 0 ? (
        <div className="vp-card">
          <div className="vp-card-head">
            <div className="vp-card-title">Derived predicates ({view.predicates.length})</div>
          </div>
          <div className="vp-card-body">
            {view.predicates.map((predicate) => (
              <div key={predicate.id} className="vp-field">
                <div className="vp-field-label">{predicate.id} · {predicate.kind}</div>
                <div className="vp-field-value">
                  <span className="vp-mono">{predicate.expr}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {view.oracleCandidates.length > 0 ? (
        <div className="vp-section">
          <div className="vp-section-head">
            <h2>Bind an independent oracle</h2>
          </div>
          {bindError ? (
            <div className="vp-callout" data-tone="fail">
              <div className="vp-callout-title">That oracle cannot be bound</div>
              <div className="vp-callout-body">{bindError}</div>
            </div>
          ) : null}
          <div className="vp-stack" data-gap="sm">
            {view.oracleCandidates.map((candidate) => (
              <div className="vp-card" key={candidate.key}>
                <div className="vp-card-head">
                  <div className="vp-card-title">{candidate.key}</div>
                </div>
                <div className="vp-card-body">{candidate.proves}</div>
                <div className="vp-card-foot">
                  {candidate.bindable ? (
                    <Btn
                      actionId={ACTION.bind}
                      variant="outline"
                      size="sm"
                      disabled={!canMutate}
                      title={canMutate ? "Bind this oracle" : BIND_PREREQ}
                      onClick={() => onBind(candidate.key)}
                    >
                      Bind this oracle
                    </Btn>
                  ) : (
                    <ToneBadge tone="unknown">not available yet</ToneBadge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {persistError ? (
        <div className="vp-callout" data-tone="fail">
          <div className="vp-callout-title">Persist refused</div>
          <div className="vp-callout-body">{persistError}</div>
        </div>
      ) : null}
      {view.admission.status !== "reject" ? (
        <div className="flex flex-wrap items-center gap-2">
          <Btn actionId={ACTION.createAdmit} variant="primary" disabled={!canMutate} onClick={onPersist}>
            {view.admission.status === "admit" ? "Create and admit" : "Save draft"}
          </Btn>
          <Btn actionId={ACTION.toQueue} variant="outline" onClick={() => onNavigate(href("queue"))}>
            Go to the decision inbox
          </Btn>
        </div>
      ) : null}
    </>
  );
}
